import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { Cog, Users, Landmark, CalendarDays, Plus, Trash2, Save, RotateCcw } from "lucide-react";
import TeamRoster from "@/pages/TeamRoster";
import Departments from "@/pages/Departments";

const TABS = [
  { k: "team", l: "Εργαζόμενοι", icon: Users },
  { k: "depts", l: "Τμήματα & Ρόλοι", icon: Landmark },
  { k: "leave", l: "Άδειες & Αργίες", icon: CalendarDays },
];
const inp = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]";

function HolidaysPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canOrg = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [leaveDays, setLeaveDays] = useState(20);
  const { data } = useQuery({ queryKey: ["holidays", year], queryFn: async () => (await api.get(`/settings/holidays?year=${year}`)).data });
  const { data: org } = useQuery({ queryKey: ["org-settings"], queryFn: async () => (await api.get("/settings/org")).data });
  useEffect(() => { if (data) setRows(data.holidays); }, [data]);
  useEffect(() => { if (org) setLeaveDays(org.annual_leave_days ?? 20); }, [org]);

  const save = useMutation({
    mutationFn: async () => {
      await api.put("/settings/holidays", { year, holidays: rows });
      if (canOrg && Number(leaveDays) !== org?.annual_leave_days) await api.put("/settings/org", { ...org, annual_leave_days: Number(leaveDays) || 0 });
    },
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["holidays"] }); qc.invalidateQueries({ queryKey: ["org-settings"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const reset = useMutation({
    mutationFn: async () => (await api.delete(`/settings/holidays?year=${year}`)).data,
    onSuccess: () => { toast.success("Επαναφορά στις επίσημες ελληνικές αργίες"); qc.invalidateQueries({ queryKey: ["holidays"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const upd = (i, p) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  return (
    <div className="space-y-5" data-testid="hr-holidays-panel">
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5">
        <h3 className="font-medium text-[#45423C]">Άδειες</h3>
        <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mt-3">Ετήσιο δικαίωμα κανονικής άδειας (ημέρες)</label>
        <input data-testid="hr-leave-days" type="number" min="0" value={leaveDays} disabled={!canOrg} onChange={(e) => setLeaveDays(e.target.value)} className={`${inp} mt-1 w-40`} />
        <p className="text-xs text-[#8A8577] mt-2">Χρησιμοποιείται για το υπόλοιπο αδειών κάθε εργαζομένου.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-[#45423C] mr-auto">Επίσημες αργίες {data?.custom ? "· προσαρμοσμένες" : "· ελληνικό ημερολόγιο"}</h3>
          <select data-testid="hr-holiday-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className={inp}>
            {[-1, 0, 1, 2].map((d) => { const y = new Date().getFullYear() + d; return <option key={y} value={y}>{y}</option>; })}
          </select>
          <button data-testid="hr-holiday-reset" onClick={() => reset.mutate()} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm text-[#45423C]"><RotateCcw className="h-4 w-4" /> Επαναφορά</button>
        </div>
        <p className="text-xs text-[#8A8577]">Περιλαμβάνει τις κινητές αργίες (Καθαρά Δευτέρα, Πάσχα, Αγίου Πνεύματος) βάσει Ορθόδοξου Πάσχα. Οι αργίες επηρεάζουν το πρόγραμμα και τις προσαυξήσεις.</p>
        <div className="divide-y divide-[#E3DED4]">
          {rows.map((r, i) => (
            <div key={`${i}-${r.date}`} className="flex items-center gap-2 py-2" data-testid={`hr-holiday-${i}`}>
              <input type="date" value={r.date} onChange={(e) => upd(i, { date: e.target.value })} className={`${inp} w-40`} data-testid={`hr-holiday-date-${i}`} />
              <input value={r.name} onChange={(e) => upd(i, { name: e.target.value })} className={`${inp} flex-1 min-w-0`} data-testid={`hr-holiday-name-${i}`} />
              <button data-testid={`hr-holiday-del-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="h-9 w-9 rounded-lg border border-[#E7CEC6] flex items-center justify-center text-[#A1493A]"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button data-testid="hr-holiday-add" onClick={() => setRows([...rows, { date: `${year}-01-01`, name: "" }])} className="rounded-xl border border-dashed border-[#D8D0C2] px-4 py-2 text-sm text-[#6F6A60] inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Προσθήκη αργίας</button>
      </div>
      <button data-testid="hr-holiday-save" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-xl bg-[#45423C] text-white px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" /> Αποθήκευση</button>
    </div>
  );
}

export default function HRSettings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "team";
  return (
    <div className="space-y-6" data-testid="hr-settings-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">HR</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Cog className="h-7 w-7" /> Ρυθμίσεις</h1>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`hr-settings-tab-${t.k}`} onClick={() => setParams({ tab: t.k })}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border ${tab === t.k ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60] bg-white"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>
      {tab === "team" && <TeamRoster embedded />}
      {tab === "depts" && <Departments embedded />}
      {tab === "leave" && <HolidaysPanel />}
    </div>
  );
}
